import React, { useState } from 'react';
import SearchBox from '../../../../components/outline/SearchBox';
import BaseForm from '../../../../components/form/BaseForm';
import ButtonList from '../../../../components/ButtonList';
import DateRangePicker from '../../../../components/datePicker/DateRangePicker';
import Select from '../../../../components/input/Select';
import InputNumber from '../../../../components/input/InputNumber';

import { DATE_FORMAT } from '../../../../constants/service/common/utilConstants';
import {
  SETTLEMENT_SEARCH_PERIOD_TYPE,
  SETTLEMENT_DATE_READY_BUTTON_LIST,
  SETTLEMENT_MONTHLY_EXECUTION_DAY,
  SETTLEMENT_MONTHLY_EXECUTION_PERIOD,
  SETTLEMENT_PERIOD_SELECT_LIST,
  SETTLEMENT_SEARCH_PERIOD_SELECT_LIST,
  SETTLEMENT_FORM_INIT_STATE,
} from '../../constants';
import { SettlementForm } from '../../types';
import { ValueOf } from '../../../../types/common/utils';
import moment from '../../../../lib/moment';
import useRouter from '../../../../hooks/common/useRouter';
import { isSettlementForm } from '../../utils/guards';
import { StyledWrapper } from './styles';
import { numberStringRegExp } from '../../../../regExp/numberStringRegExp';

const SettlementListSearchBox = (): JSX.Element => {
  const router = useRouter();
  const { pathname, query } = router;

  const [form, setForm] = useState<SettlementForm>(isSettlementForm(query) ? query : SETTLEMENT_FORM_INIT_STATE);

  const onClear = (): void => {
    router.push(pathname, {});
  };

  const onSubmit = (form: SettlementForm): void => {
    router.push(pathname, { query: form });
  };

  const setDate = (dates?: [string, string]): void => {
    const [startDate, endDate] = dates ?? [];

    setForm((prev) => ({
      ...prev,
      searchPeriodStartDate: startDate,
      searchPeriodEndDate: endDate,
    }));
  };

  return (
    <StyledWrapper>
      <SearchBox
        className="coupon-list-search-box"
        onSearch={(): void => {
          onSubmit(form);
        }}
        onClear={(): void => {
          onClear();
          setForm(SETTLEMENT_FORM_INIT_STATE);
        }}
      >
        {/*정산 예정일*/}
        <BaseForm
          label={
            <Select
              list={SETTLEMENT_SEARCH_PERIOD_SELECT_LIST}
              onChange={(searchPeriodType: ValueOf<typeof SETTLEMENT_SEARCH_PERIOD_TYPE>): void =>
                setForm((prev) => ({ ...prev, searchPeriodType }))
              }
              value={form.searchPeriodType}
              size="large"
            />
          }
          content={
            <div className="quick-date-picker">
              {/*날짜 선택 빠른 키*/}
              <ButtonList
                current={form.quickDate}
                list={SETTLEMENT_DATE_READY_BUTTON_LIST}
                onClick={(option: (typeof SETTLEMENT_DATE_READY_BUTTON_LIST)[number]): void => {
                  setForm((prev) => ({ ...prev, quickDate: option.id }));

                  const selectedPeriod = SETTLEMENT_DATE_READY_BUTTON_LIST.find(({ id }) => id === option.id);

                  if (selectedPeriod && selectedPeriod.id === 'ALL') {
                    setDate(undefined);
                    return;
                  }

                  if (selectedPeriod) {
                    setDate([
                      moment().subtract(selectedPeriod.amount, selectedPeriod.unit).format(DATE_FORMAT.YMD_DASH_FORMAT),
                      moment().format(DATE_FORMAT.YMD_DASH_FORMAT),
                    ]);
                  }
                }}
              />

              {/*날짜 선택*/}
              <DateRangePicker
                value={{ startDate: form.searchPeriodStartDate, endDate: form.searchPeriodEndDate }}
                outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
                onSelect={(period: { startDate: string; endDate: string }): void => {
                  setForm((prev) => ({ ...prev, quickDate: undefined }));
                  setDate([period.startDate, period.endDate]);
                }}
              />
            </div>
          }
        />

        <BaseForm
          label="정산 주기 타입"
          content={
            <ButtonList
              current={form.periodType}
              list={SETTLEMENT_PERIOD_SELECT_LIST}
              onClick={(option: (typeof SETTLEMENT_PERIOD_SELECT_LIST)[number]): void => {
                setForm((prev) => ({ ...prev, periodType: option.id }));
              }}
            />
          }
        />

        <BaseForm
          label="월간 정산 주기"
          content={
            <InputNumber
              value={
                typeof form.monthlyExecutionPeriod === 'string'
                  ? Number(form.monthlyExecutionPeriod)
                  : form.monthlyExecutionPeriod
              }
              onChange={(value): void => {
                if (value === null) {
                  setForm((prev) => ({ ...prev, monthlyExecutionPeriod: undefined }));
                  return;
                }

                const monthlyExecutionPeriod = String(value);

                if (!numberStringRegExp.test(String(value))) {
                  return;
                }

                setForm((prev) => ({ ...prev, monthlyExecutionPeriod }));
              }}
              min={SETTLEMENT_MONTHLY_EXECUTION_PERIOD.MIN}
              max={SETTLEMENT_MONTHLY_EXECUTION_PERIOD.MAX}
              addonAfter="개월"
            />
          }
        />

        <BaseForm
          label="월간 정산일"
          content={
            <InputNumber
              value={
                typeof form.monthlyExecutionDay === 'string'
                  ? Number(form.monthlyExecutionDay)
                  : form.monthlyExecutionDay
              }
              onChange={(value): void => {
                if (value === null) {
                  setForm((prev) => ({ ...prev, monthlyExecutionDay: undefined }));
                  return;
                }

                if (!numberStringRegExp.test(String(value))) {
                  return;
                }

                const monthlyExecutionDay = String(value);
                setForm((prev) => ({ ...prev, monthlyExecutionDay }));
              }}
              min={SETTLEMENT_MONTHLY_EXECUTION_DAY.MIN}
              max={SETTLEMENT_MONTHLY_EXECUTION_DAY.MAX}
              addonAfter="일"
            />
          }
        />
      </SearchBox>
    </StyledWrapper>
  );
};

export default SettlementListSearchBox;
